import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { getRoles, sendEmails } from '../../../api/apiCall';
import { useAuth } from '../../../auth/auth';
import { toast } from 'react-toastify';
import AccessForbidden from '../../Access/AccessForbidden';
import Loading from '../../../components/Loading/Loading';

const SendEmails = () => {
    const {state} = useAuth();
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [, setRoles] = useState<any>([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);

    const handleFileChange = (event: any) => {
        const file = event.target.files[0];
        setError('');

        if (!file) {
            setSelectedFile(null);
            return;
        }

        const allowedExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            setError('Please select a valid Excel file (.xlsx or .xls)');
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setSelectedFile(file);
    };

    const handleSubmit = async () => {
        setLoading(true);
        if (!selectedFile) {
            setError('No file selected');
            setLoading(false);
            return;
        }
        await sendEmails(state.user?.token, selectedFile);
        toast.success('Emails sent successfully')
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setLoading(false);
    };

    const getAllRoles = async () => {
        const response = await getRoles(state.user?.token);
        if (response) {
            setRoles(response.data);
        } else {
            setIsAdmin(true);
        }
        setLoadingPage(false);
    };

    useEffect(() => {
        getAllRoles();
    }, []);

    if (loadingPage) {
        return <Loading />;
    }

    if (isAdmin) {
        return <AccessForbidden />;
    }

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f7fa',
                p: 3,
            }}
        >
            <Paper
                sx={{
                    boxShadow:'none',
                    marginBottom: 30,
                    maxWidth: 500,
                    width: '100%',
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                }}
            >
                <Typography variant="h5" gutterBottom fontWeight="bold">
                    Attach File
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Only .xlsx or .xls files are allowed
                </Typography>

                <Stack spacing={3} alignItems="center">
                    <input
                        accept=".xlsx, .xls"
                        id="excel-file-input"
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <label htmlFor="excel-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<AttachFileIcon />}
                            sx={{ textTransform: 'none' }}
                            style={{ border: 'none', marginBottom: 0 }}
                        >
                            Choose Excel File
                        </Button>
                    </label>

                    {selectedFile && (
                        <Alert severity="success" icon={<CloudUploadIcon />} sx={{ width: '75%' }}>
                            <strong>Selected:</strong> {selectedFile.name}
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedFile || loading}
                        fullWidth
                        sx={{ mt: 2, textTransform: 'none', py: 1, boxShadow: "none", width: "80%" }}
                        style={{ backgroundColor: loading ? '#ccc' : '#1976d2', color: '#fff' }}
                    >
                        {loading ? 'Sending...' : 'Send Emails'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export default SendEmails;
